import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Badge, useTheme, useMediaQuery } from '@mui/material';
import Taskbar from '../os/Taskbar';
import DashboardWidget from '../os/DashboardWidget';
import DesktopIcon from '../os/DesktopIcon';
import OSWindow from '../os/OSWindow';
import DesktopGrid from '../os/DesktopGrid';
import TourRunner from '../common/TourRunner';
import { TourType } from '../../config/TourConfigurations';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { designTokens } from '../../theme/designTokens';
import ClassIcon from '@mui/icons-material/Class';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SettingsIcon from '@mui/icons-material/Settings';
import SchoolIcon from '@mui/icons-material/School';
import DescriptionIcon from '@mui/icons-material/Description';
import FaceIcon from '@mui/icons-material/Face';
import AssessmentIcon from '@mui/icons-material/Assessment';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BarChartIcon from '@mui/icons-material/BarChart';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ErrorIcon from '@mui/icons-material/Error';
import ChatIcon from '@mui/icons-material/Chat';
import SecurityIcon from '@mui/icons-material/Security';
import api from '../../services/api';
import { useImageBrightness } from '../../hooks/useImageBrightness';

/**
 * Props for the AdminOSLayout component
 */
interface AdminOSLayoutProps {
    /** The content to render within the OS window */
    children?: React.ReactNode;
}

/**
 * Layout component for the Admin interface, designed to mimic a desktop operating system.
 * It includes a desktop area with icons, a taskbar, and a window system for content.
 *
 * @param props - The props for the component
 * @returns The Admin OS Layout UI
 */
const AdminOSLayout: React.FC<AdminOSLayoutProps> = ({ children }) => {
    const location = useLocation();
    const isDashboard = location.pathname === '/dashboard';
    const [currentTour, setCurrentTour] = useState<TourType | null>(null);
    const { user, logout } = useAuth();
    const { showToast } = useToast();
    const [currentTime, setCurrentTime] = useState(new Date());
    const navigate = useNavigate();
    const [orgLogo, setOrgLogo] = useState<string | null>(null);
    const [pendingQuestionsCount, setPendingQuestionsCount] = useState(0);
    const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (user?.role === 'learner') {
            navigate('/learner/dashboard');
        }
    }, [user, navigate]);

    // Fetch Organization Logo
    useEffect(() => {
        const fetchOrgLogo = async () => {
            if (user?.organizationId) {
                try {
                    // OF Admins use the /my/organization endpoint, super-admins use /:id
                    const orgId = typeof user.organizationId === 'object'
                        ? (user.organizationId as any)?._id
                        : user.organizationId;
                    const endpoint = user.role === 'of-admin'
                        ? '/api/organizations/my/organization'
                        : `/api/organizations/${orgId}`;

                    const res = await api.get(endpoint);
                    if (res.data && res.data.logoUrl) {
                        setOrgLogo(res.data.logoUrl);
                    }
                } catch (error) {
                    console.error('Error fetching organization logo', error);
                    showToast('Erreur lors du chargement du logo de l\'organisation', 'error');
                }
            }
        };

        const fetchPendingQuestions = async () => {
            if (isAppVisible('questions')) {
                try {
                    const res = await api.get('/api/questions/count');
                    setPendingQuestionsCount(res.data.count);
                } catch (err) {
                    console.error("Error fetching pending questions count", err);
                }
            }
        };

        const fetchUnreadMessages = async () => {
            if (isAppVisible('chat')) {
                try {
                    const res = await api.get('/api/chat/unread-count');
                    setUnreadMessagesCount(res.data.count);
                } catch (err) {
                    console.error("Error fetching unread messages count", err);
                }
            }
        };

        fetchOrgLogo();
        fetchPendingQuestions();
        fetchUnreadMessages();
    }, [user?.organizationId, user?.role, user?.authorizedApps, user?.preferences?.enabledApps]);

    const handleTourEnd = () => {
        setCurrentTour(null);
    };

    const startTour = (tourType: TourType) => {
        setCurrentTour(tourType);
    };

    const getTitle = () => {
        const path = location.pathname;
        if (path.includes('/parcours')) return 'Parcours';
        if (path.includes('/courses')) return 'Cours';
        if (path.includes('/marketing')) return 'Marketing';
        if (path.includes('/users')) return 'Utilisateurs';
        if (path.includes('/organizations')) return 'Organismes';
        if (path.includes('/enrollment')) return 'Inscriptions';
        if (path.includes('/settings')) return 'Paramètres';
        if (path.includes('/learners')) return 'Apprenants';
        if (path.includes('/mon-of')) return 'Mon OF';
        if (path.includes('/block-evaluation')) return 'Évaluation de Bloc';
        if (path.includes('/questions')) return 'Questions';
        if (path.includes('/media-library')) return 'Médiathèque';
        if (path.includes('/contracts')) return 'Contrats';
        if (path.includes('/admin/chat')) return 'Messagerie';
        return 'Administration';
    };

    const background = user?.preferences?.osBackground || '/default-bg.jpg';
    const taskbarContrast = useImageBrightness(background);

    // Helper function to check if app should be visible
    const isAppVisible = (appId: string) => {
        // @ts-ignore - Dynamic access to authorizedApps
        const isAuthorized = user?.authorizedApps?.[appId] ?? false;
        // @ts-ignore - Dynamic access to enabledApps
        const isEnabled = user?.preferences?.enabledApps?.[appId] ?? true;
        return isAuthorized && isEnabled;
    };

    return (
        <Box
            id="admin-os-layout"
            sx={{
                height: '100vh',
                width: '100vw',
                backgroundImage: `url(${background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <TourRunner tourType={currentTour} onTourEnd={handleTourEnd} />

            {/* Desktop Area - Scrollable on mobile */}
            <Box
                id="desktop-area"
                sx={{
                    p: isMobile ? 1 : 4,
                    height: isMobile ? 'calc(100vh - 80px)' : '100%', // Leave space for taskbar on mobile
                    position: 'relative',
                    zIndex: 1,
                    overflowY: isMobile ? 'auto' : 'visible',
                    overflowX: 'hidden',
                    pb: isMobile ? '100px' : 0, // Extra padding for taskbar
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
                }}
            >
                {/* Logo Area */}
                <Box sx={{
                    position: 'absolute',
                    top: isMobile ? 10 : 20,
                    left: isMobile ? 10 : 20,
                    zIndex: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 1 : 2
                }}>
                    <img src="/logo.png" alt="Edu-JALM Logo" style={{ height: isMobile ? 40 : 60, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }} />
                    {orgLogo && (
                        <>
                            <Typography variant="h5" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 'bold', mx: isMobile ? 0.5 : 1, fontSize: isMobile ? '1.2rem' : '1.5rem' }}>X</Typography>
                            <img src={orgLogo} alt="Organization Logo" style={{ height: isMobile ? 35 : 50, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))', maxWidth: isMobile ? 100 : 150, objectFit: 'contain' }} />
                        </>
                    )}
                </Box>

                {/* Widget Area */}
                <Box id="dashboard-widget" sx={{
                    position: isMobile ? 'relative' : 'absolute',
                    top: isMobile ? 'auto' : 40,
                    right: isMobile ? 'auto' : 40,
                    mt: isMobile ? 8 : 0, // Push down below absolute logo
                    width: isMobile ? '100%' : 'auto',
                    display: isMobile ? 'flex' : 'block',
                    justifyContent: 'center',
                    mb: isMobile ? 3 : 0,
                    zIndex: 5
                }}>
                    <DashboardWidget />
                </Box>

                {/* Desktop Icons Grid */}
                <DesktopGrid
                    icons={[
                        { id: 'cursus', label: 'Parcours', icon: <ClassIcon />, path: '/parcours' },
                        { id: 'courses', label: 'Cours', icon: <LibraryBooksIcon />, path: '/courses' },
                        { id: 'marketing', label: 'Marketing', icon: <LocalOfferIcon />, path: '/marketing' },
                        { id: 'users', label: 'Utilisateurs', icon: <PeopleIcon />, path: '/users' },
                        { id: 'apprenants', label: 'Apprenants', icon: <FaceIcon />, path: '/admin/apprenants' },
                        { id: 'monof', label: 'Mon OF', icon: <BusinessIcon />, path: '/mon-of' },
                        { id: 'block-evaluation', label: 'Évaluation de Bloc', icon: <AssignmentIcon />, path: '/block-evaluation' },
                        { id: 'organizations', label: 'Organismes', icon: <BusinessIcon />, path: '/organizations' },
                        { id: 'enrollment', label: 'Inscriptions', icon: <AssignmentIcon />, path: '/enrollment' },
                        { id: 'learnerSpace', label: 'Espace Apprenant', icon: <SchoolIcon />, path: '/learner' },
                        { id: 'reports', label: 'Rapports', icon: <AssessmentIcon />, path: '/reports' },
                        {
                            id: 'questions',
                            label: 'Questions',
                            icon: (
                                <Badge badgeContent={pendingQuestionsCount} color="error" overlap="circular" sx={{ '& .MuiBadge-badge': { right: -5, top: -5 } }}>
                                    <QuestionAnswerIcon />
                                </Badge>
                            ),
                            path: '/questions'
                        },
                        { id: 'trainers', label: 'Nos Formateurs', icon: <SupervisorAccountIcon />, path: '/trainers' },
                        { id: 'statistics', label: 'Statistiques', icon: <BarChartIcon />, path: '/statistics' },
                        { id: 'satisfaction', label: 'Satisfaction', icon: <ThumbUpIcon />, path: '/satisfaction' },
                        { id: 'settings', label: 'Paramètres', icon: <SettingsIcon />, path: '/settings' },
                        { id: 'media', label: 'Médiathèque', icon: <PermMediaIcon />, path: '/media-library' },
                        { id: 'contracts', label: 'Contrats', icon: <AssignmentIcon />, path: '/contracts' },
                        { id: 'course-review', label: 'Cours à Réviser', icon: <ErrorIcon sx={{ color: '#f59e0b' }} />, path: '/course-review' },
                        { id: 'error', label: 'Errors', icon: <ErrorIcon />, path: '/admin/error-reporter' },
                        {
                            id: 'chat',
                            label: 'Messagerie',
                            icon: (
                                <Badge badgeContent={unreadMessagesCount} color="error" overlap="circular" sx={{ '& .MuiBadge-badge': { right: -5, top: -5 } }}>
                                    <ChatIcon />
                                </Badge>
                            ),
                            path: '/admin/chat'
                        },
                        { id: 'security', label: 'Sécurité OF', icon: <SecurityIcon />, path: '/admin/security' },

                        ...(import.meta.env.DEV && user?.role === 'super-admin' ? [{
                            id: 'api-docs',
                            label: 'API Docs',
                            icon: <DescriptionIcon />,
                            path: '/api-docs'
                        }] : [])
                    ].filter(item => isAppVisible(item.id))}
                    userPreferences={user?.preferences}
                />
            </Box>

            {/* Window Overlay */}
            {
                !isDashboard && children && (
                    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10 }}>
                        <OSWindow
                            title={getTitle()}
                            initialMaximized={
                                !location.pathname.includes('/bloc/') &&
                                !location.pathname.includes('/category/') &&
                                !(location.pathname.includes('/parcours/') && location.pathname.split('/').length > 2)
                            }
                        >
                            {children}
                        </OSWindow>
                    </Box>
                )
            }

            {/* Taskbar */}
            <Box id="taskbar">
                <Taskbar onStartTour={startTour} contrastMode={taskbarContrast} />
            </Box>
        </Box >
    );
};

export default AdminOSLayout;
